// react
import { useState } from 'react';

// i18n

// mui
import {
	alpha,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

// icons
import {
	Close as IconClose,
	NotificationsActive as IconNotification,
	ReportProblem as IconWarning,
} from '@mui/icons-material';

// data
import { COLORS, ConditionContext } from '../../../data';

// Props & Types

export type { ConditionContext };

export interface DialogOverrideConditionProps {
	condition: ConditionContext | null;
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (reason: string) => void;
}

// Component

export const DialogOverrideCondition = ({
	condition,
	isOpen,
	onClose,
	onConfirm,
}: DialogOverrideConditionProps) => {
	// hooks

	// state
	const [acknowledged, setAcknowledged] = useState(false);
	const [reason, setReason] = useState('');

	// handlers
	const handleClose = () => {
		setAcknowledged(false);
		setReason('');
		onClose();
	};

	const handleConfirm = () => {
		if (reason.trim() && acknowledged) {
			onConfirm(reason.trim());
			handleClose();
		}
	};

	if (!condition) return null;

	return (
		<Dialog
			open={isOpen}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 3,
					overflow: 'hidden',
				},
			}}>
			{/* Header */}
			<DialogTitle
				sx={{
					bgcolor: COLORS.warning.light,
					borderBottom: '1px solid',
					borderColor: COLORS.warning.main,
					p: 2.5,
				}}>
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Stack direction="row" alignItems="center" spacing={2}>
						<IconWarning
							sx={{
								color: COLORS.warning.main,
								fontSize: 28,
							}}
						/>
						<Box>
							<Typography variant="h6" sx={{ fontWeight: 700 }}>
								Condition Override Warning
							</Typography>
							<Typography variant="body2" sx={{ color: COLORS.warning.dark }}>
								You are about to proceed without meeting this condition
							</Typography>
						</Box>
					</Stack>
					<IconButton onClick={handleClose} size="small">
						<IconClose />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent sx={{ p: 3 }}>
				{/* Condition Info */}
				<Paper
					elevation={0}
					sx={{
						bgcolor: 'grey.50',
						border: '1px solid',
						borderColor: 'divider',
						borderRadius: 2,
						mb: 3,
						p: 2,
					}}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
						Condition: {condition.conditionName}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						{condition.description}
					</Typography>
					<Stack direction="row" spacing={3}>
						<Box>
							<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
								CONTRACT
							</Typography>
							<Typography variant="body2">{condition.contractNum}</Typography>
						</Box>
						<Box>
							<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
								SUPPLIER
							</Typography>
							<Typography variant="body2">{condition.supplierName}</Typography>
						</Box>
						<Box>
							<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
								ACTIVITY
							</Typography>
							<Typography variant="body2">{condition.activityName}</Typography>
						</Box>
					</Stack>
				</Paper>

				{/* Override Form */}
				<Paper
					elevation={0}
					sx={{
						bgcolor: alpha(COLORS.warning.main, 0.08),
						border: '1px solid',
						borderColor: COLORS.warning.main,
						borderRadius: 2,
						mb: 3,
						p: 2,
					}}>
					<Stack direction="row" alignItems="flex-start" spacing={2}>
						<IconNotification sx={{ color: COLORS.warning.main, mt: 0.5 }} />
						<Box>
							<Typography variant="subtitle2" sx={{ color: COLORS.warning.dark, fontWeight: 600 }}>
								Notification Will Be Sent
							</Typography>
							<Typography variant="body2" sx={{ color: COLORS.warning.dark, mt: 0.5 }}>
								If you proceed, a notification will be sent to the responsible person in the PIU,
								and this exception will be recorded in the system.
							</Typography>
						</Box>
					</Stack>
				</Paper>

				<TextField
					autoFocus
					fullWidth
					label="Reason for Override"
					multiline
					onChange={(e) => setReason(e.target.value)}
					placeholder="Please provide a detailed justification for proceeding without meeting this condition..."
					required
					rows={4}
					value={reason}
					sx={{ mb: 2 }}
				/>

				<FormControlLabel
					control={
						<Checkbox
							checked={acknowledged}
							onChange={(e) => setAcknowledged(e.target.checked)}
							color="warning"
						/>
					}
					label={
						<Typography variant="body2">
							I understand that this exception will be recorded and the responsible PIU manager will
							be notified
						</Typography>
					}
				/>
			</DialogContent>

			<DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
				<Button onClick={handleClose} color="inherit">
					Cancel
				</Button>
				<Button
					color="warning"
					disabled={!reason.trim() || !acknowledged}
					onClick={handleConfirm}
					variant="contained"
					sx={{
						bgcolor: COLORS.warning.main,
						'&:hover': {
							bgcolor: COLORS.warning.dark,
						},
					}}>
					Proceed Anyway
				</Button>
			</DialogActions>
		</Dialog>
	);
};
